import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

function formatSeconds(sec) {
  if (!sec || sec <= 0) return '—';
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
}

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString([], {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

/**
 * Staff performance table — sortable by tickets served or avg service time.
 *
 * Props:
 *   rows: [{ userId, email, ticketsServed, avgServiceSeconds, lastServedAt }]
 */
export default function StaffTable({ rows }) {
  const { t } = useTranslation();
  const [sortBy, setSortBy] = useState('ticketsServed');
  const [sortDir, setSortDir] = useState('desc');

  const sorted = useMemo(() => {
    const copy = [...(rows || [])];
    copy.sort((a, b) => {
      const av = a[sortBy] ?? 0;
      const bv = b[sortBy] ?? 0;
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return copy;
  }, [rows, sortBy, sortDir]);

  const toggleSort = (col) => {
    if (sortBy === col) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(col);
      setSortDir('desc');
    }
  };

  const arrow = (col) => (sortBy === col ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '');

  if (!rows || rows.length === 0) {
    return <div style={emptyStyle}>{t('analytics.staff.empty')}</div>;
  }

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={tableStyle}>
        <thead>
          <tr>
            <th style={thStyle}>{t('analytics.staff.email')}</th>
            <th
              style={{ ...thStyle, ...sortableThStyle }}
              onClick={() => toggleSort('ticketsServed')}
              aria-sort={sortBy === 'ticketsServed' ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
            >
              {t('analytics.staff.ticketsServed')}{arrow('ticketsServed')}
            </th>
            <th
              style={{ ...thStyle, ...sortableThStyle }}
              onClick={() => toggleSort('avgServiceSeconds')}
              aria-sort={sortBy === 'avgServiceSeconds' ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
            >
              {t('analytics.staff.avgService')}{arrow('avgServiceSeconds')}
            </th>
            <th style={thStyle}>{t('analytics.staff.lastServed')}</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((r) => (
            <tr key={r.userId || r.email}>
              <td style={tdStyle}>{r.email || t('analytics.staff.unknownUser')}</td>
              <td style={tdNumStyle}>{r.ticketsServed}</td>
              <td style={tdNumStyle}>{formatSeconds(r.avgServiceSeconds)}</td>
              <td style={tdStyle}>{formatDate(r.lastServedAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const tableStyle = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '0.9rem',
  color: '#F8FAFC',
};
const thStyle = {
  textAlign: 'left',
  padding: '0.6rem 0.75rem',
  fontSize: '0.75rem',
  fontWeight: 700,
  color: '#94A3B8',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  borderBottom: '1px solid #334155',
  whiteSpace: 'nowrap',
};
const sortableThStyle = {
  cursor: 'pointer',
  userSelect: 'none',
};
const tdStyle = {
  padding: '0.65rem 0.75rem',
  borderBottom: '1px solid #1E293B',
};
const tdNumStyle = {
  ...tdStyle,
  fontVariantNumeric: 'tabular-nums',
  fontWeight: 600,
};
const emptyStyle = {
  textAlign: 'center',
  padding: '2rem 1rem',
  color: '#64748B',
  fontSize: '0.9rem',
};
